// Renders the transcript. Replies appear in full, dimmed, and each word lights up as it is
// spoken; the user's words stream in while they talk. Ported from live-voice-ai, including its
// incremental rendering (cost stays flat however long the conversation gets) and its
// follow-the-bottom scrolling that never fights the user's finger.

import { thatWasMeButton } from "./guests-view";
import { clipButton, type ClipPlayer } from "./mine";
import type { AttachmentInfo, SpeakerSpan } from "./protocol";
import type { Position } from "./scheduler";
import { injectStyles } from "./styles";
import { splitMessages, type AssistantEntry, type Entry, type IgnoredEntry, type TranscriptModel } from "./transcript";

type Scrollable = Pick<HTMLElement, "scrollTop" | "scrollHeight" | "clientHeight">;

/** Is the view (close enough to) the bottom? Within `slack` px counts. */
export function atBottom(el: Scrollable, slack = 48): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= slack;
}

/**
 * Who controls the scroll position. Auto-follow the newest text until the user touches, drags
 * or wheels; while their finger is down we never move the view; after they let go, following
 * resumes only if they left it at the bottom (or when a new turn starts).
 */
export class FollowState {
  private follow = true;
  private touching = false;
  private userControlled = false;

  userStart(): void {
    this.touching = true;
    this.userControlled = true;
    this.follow = false;
  }

  userEnd(): void {
    this.touching = false;
  }

  /** Scrolling has come to rest; `atBottom` is where it ended. */
  settled(atBottom: boolean): void {
    if (this.touching || !this.userControlled) return;
    this.follow = atBottom;
    if (atBottom) this.userControlled = false;
  }

  newTurn(): void {
    this.follow = true;
    this.userControlled = false;
  }

  shouldAutoScroll(): boolean {
    return this.follow && !this.touching;
  }
}

function scrollParent(el: HTMLElement): HTMLElement {
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    const o = getComputedStyle(n).overflowY;
    if (o === "auto" || o === "scroll") return n;
  }
  return document.scrollingElement as HTMLElement;
}

export interface TranscriptView {
  destroy(): void;
}

export interface TranscriptActions {
  /**
   * 1.10, "That was me": ignored speech (a "not your voice" line, or a one-time "Guest 1 spoke"
   * marker) was the user (`client.speechMine`). Without it, no button.
   */
  thatWasMe?(ignoredId: string): Promise<void>;
  /**
   * Kit 0.12: send a marked line that was not sent on its own (it was not the latest thing the
   * user said) to the assistant (`client.speechMineSend`). Without it, no "Send to assistant".
   */
  send?(ignoredId: string): Promise<void>;
  /** 1.10: plays an ignored line's clip (a play/stop button beside "That was me"). */
  clips?: ClipPlayer;
  /**
   * Whether this UI may act on `ignoredId` (a phone only on speech heard on its own mic): false
   * leaves out both Play and "That was me". Without it, every line with an id offers them.
   */
  mayAct?(ignoredId: string): boolean;
}

/**
 * Mount the transcript into `list` (an `<ol>` or `<ul>`; give it or a parent `overflow: auto`).
 * `progress` is the voiced reply's live playback position (`client.progressNow()`).
 */
export function mountTranscript(
  list: HTMLElement,
  model: TranscriptModel,
  progress: () => Position | null,
  actions: TranscriptActions = {},
): TranscriptView {
  injectStyles();
  list.classList.add("vk-transcript");
  const follow = new FollowState();
  let scroller: HTMLElement | null = null;
  const getScroller = () => (scroller ??= scrollParent(list));
  const toBottom = () => {
    const sc = getScroller();
    sc.scrollTop = sc.scrollHeight;
  };
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let wheelTimer: ReturnType<typeof setTimeout> | null = null;
  const onScroll = () => {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => follow.settled(atBottom(getScroller())), 150); // after momentum stops
  };
  const area = list.parentElement ?? list;
  const ac = new AbortController();
  const opts = { passive: true, signal: ac.signal };
  for (const el of new Set([list, area])) el.addEventListener("scroll", onScroll, opts);
  area.addEventListener("touchstart", () => follow.userStart(), opts);
  area.addEventListener("touchend", () => { follow.userEnd(); onScroll(); }, opts);
  area.addEventListener("touchcancel", () => { follow.userEnd(); onScroll(); }, opts);
  area.addEventListener("pointerdown", (e) => e.pointerType === "mouse" && follow.userStart(), { signal: ac.signal });
  window.addEventListener("pointerup", (e) => { if (e.pointerType === "mouse") { follow.userEnd(); onScroll(); } }, { signal: ac.signal });
  area.addEventListener("wheel", () => {
    follow.userStart();
    if (wheelTimer) clearTimeout(wheelTimer);
    wheelTimer = setTimeout(() => { follow.userEnd(); onScroll(); }, 250);
  }, opts);
  let lastCount = 0;

  // Each message has its own <li> (a reply of several messages, one per message); only entries
  // whose revision changed are rebuilt, at most once per animation frame.
  const rendered = new Map<Entry, { lis: HTMLLIElement[]; rev: number }>();
  const spoken = new WeakMap<HTMLElement, { spans: HTMLElement[]; next: number }>();

  /** Whether this UI offers anything on `id` (Play or "That was me"; none on another device's mic). */
  const canAct = (id: string | undefined): id is string =>
    !!id && (!!actions.clips || !!actions.thatWasMe) && (actions.mayAct?.(id) ?? true);

  /**
   * Play and "That was me" for one ignored speech, side by side, or null when there is nothing
   * to offer (no id, an old kit, or speech this UI may not act on, e.g. another device's mic).
   * `describedBy`: what tells them apart from their neighbours ("2 of 3"), for screen readers.
   */
  const lineActions = (id: string | undefined, describedBy?: string): HTMLElement | null => {
    if (!canAct(id)) return null;
    const box = document.createElement("span");
    box.className = "vk-line-actions";
    if (actions.clips) box.append(clipButton(id, actions.clips));
    if (actions.thatWasMe) box.append(thatWasMeButton(id, actions.thatWasMe));
    if (describedBy) for (const b of box.children) b.setAttribute("aria-describedby", describedBy);
    return box;
  };

  // Markers that add up several ("Guest 1 spoke, 3 times") the user opened: each time is then
  // its own line with its own Play and "That was me". Kept by entry, so a group stays open when
  // it is redrawn and when the same guest speaks again (that adds to the same entry).
  const expanded = new WeakSet<Entry>();

  /** The ignored speech of each of a marker's `n` times, oldest first (an old kit sent none). */
  const timesOf = (e: IgnoredEntry, n: number): Array<string | undefined> => {
    const ids = e.ignoredIds ?? [];
    const missing = n - ids.length;
    return Array.from({ length: n }, (_, i) => (i >= missing ? ids[i - missing] : undefined));
  };

  /**
   * A marker that adds up `n` times: a button that opens it into one line per time. Opening and
   * closing change this same button (`aria-expanded`) and add or remove the lines under it, so a
   * screen reader hears the change and keeps its place.
   */
  const markerGroup = (li: HTMLLIElement, e: IgnoredEntry, label: string, times: Array<string | undefined>): HTMLLIElement => {
    const n = times.length;
    li.classList.add("group");
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "vk-group-toggle";
    const pill = document.createElement("span");
    pill.className = "vk-pill";
    pill.append(document.createTextNode(`${label} · ${n}×`), chevron());
    toggle.append(pill);
    li.append(toggle);
    let items: HTMLElement | null = null;
    const show = (open: boolean) => {
      li.classList.toggle("open", open);
      toggle.setAttribute("aria-expanded", String(open));
      toggle.title = open ? "Hide each time" : "Show each time, to play it or mark it as yours";
      items?.remove();
      items = null;
      toggle.removeAttribute("aria-controls");
      if (!open) return;
      items = document.createElement("div");
      items.className = "vk-group-items";
      items.id = `vk-group-${++groupSeq}`;
      items.setAttribute("role", "group");
      items.setAttribute("aria-label", `${label}, ${n} times`);
      toggle.setAttribute("aria-controls", items.id);
      times.forEach((id, i) => {
        const row = document.createElement("div");
        row.className = "vk-group-item";
        const text = document.createElement("span");
        text.className = "vk-group-label";
        text.textContent = label;
        const nth = document.createElement("span");
        nth.className = "vk-group-n";
        nth.id = `${items!.id}-${i + 1}`;
        nth.textContent = `${i + 1} of ${n}`;
        row.append(text, nth);
        const acts = lineActions(id, nth.id);
        if (acts) row.append(acts);
        items!.append(row);
      });
      li.append(items);
    };
    show(expanded.has(e));
    toggle.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const open = !expanded.has(e);
      if (open) expanded.add(e);
      else expanded.delete(e);
      show(open);
      if (open) li.scrollIntoView?.({ block: "nearest" });
    });
    return li;
  };

  /**
   * An entry's <li>s: one, or one per message of a reply of several (`MESSAGE_BREAK`). `said`: how
   * many of the reply's words were already lit (kept when an entry is rebuilt).
   */
  const build = (e: Entry, said = 0): HTMLLIElement[] =>
    e.role === "assistant" && (e.text || e.error) ? buildReply(e, said) : [buildLine(e)];

  const buildLine = (e: Entry): HTMLLIElement => {
    const li = document.createElement("li");
    if (e.role === "user") {
      li.className = e.partial ? "user partial" : "user";
      if (e.mine) {
        // 1.10: the user said "That was me": theirs now, with a quiet note why it moved.
        li.classList.add("mine");
        li.dataset.ignored = e.mine;
        li.title = "You marked this \u201cThat was me\u201d";
      }
      if (e.attachments?.length) li.append(attachmentList(e.attachments));
      if (e.spans?.length) li.append(...spanNodes(e.spans, model));
      else if (e.text || !e.attachments?.length) li.append(document.createTextNode(e.text || (e.mine ? "Your words" : "")));
      if (e.mine && e.sentLate) li.append(sentLateLabel());
      if (e.mine && e.unsent && actions.send && (actions.mayAct?.(e.mine) ?? true)) {
        // In the same action group as Play and "That was me" (its own row, like theirs).
        const box = document.createElement("span");
        box.className = "vk-line-actions";
        box.append(sendButton(e.mine, actions.send));
        li.append(box);
      }
      return li;
    }
    if (e.role === "ignored" && e.marker) {
      // Someone else spoke (1.8): a quiet marker; their words are in the guests panel.
      li.className = "ignored marker";
      li.dataset.reason = e.reason;
      if (e.guestId) li.dataset.guest = e.guestId;
      const n = e.count ?? 1;
      const label = `${model.guestLabel(e.guestId)} spoke`;
      li.title = "Not your voice: not sent to the assistant. What they said is under Guests.";
      // Several times: it opens into one line per time, where there is something to do on one.
      const times = n > 1 ? timesOf(e, n) : [];
      if (times.some(canAct)) return markerGroup(li, e, label, times);
      li.textContent = n > 1 ? `${label} · ${n}×` : label;
      const acts = lineActions(e.ignoredIds?.length === 1 ? e.ignoredIds[0] : undefined);
      if (acts) li.append(acts);
      return li;
    }
    if (e.role === "ignored") {
      // Not the user's voice: dimmed, labelled, and never part of the conversation.
      li.className = "ignored";
      li.dataset.reason = e.reason;
      const tag = document.createElement("span");
      tag.className = "tag";
      tag.textContent = e.guestId ? `${model.guestLabel(e.guestId)} \u00b7 not your voice` : "Ignored: not your voice";
      if (e.speaker) tag.title = `Voice match ${e.speaker.score.toFixed(2)} (needs ${e.speaker.threshold.toFixed(2)})`;
      li.append(tag, document.createTextNode(e.text));
      const acts = lineActions(e.ignoredIds?.[0]);
      if (acts) li.append(acts);
      return li;
    }
    if (!e.text && !e.error) {
      // Nothing to show yet: a quiet "thinking" bubble while the brain works, and nothing at
      // all once it ended without text (never an empty bubble, never labelled interrupted).
      li.className = "assistant thinking";
      li.dataset.key = e.key;
      li.dataset.reply = String(e.replyId);
      li.hidden = !model.isThinking(e);
      li.setAttribute("aria-label", "Thinking");
      const dots = document.createElement("span");
      dots.className = "dots";
      dots.setAttribute("aria-hidden", "true");
      for (let i = 0; i < 3; i++) dots.append(document.createElement("i"));
      li.append(dots);
      return li;
    }
    return li;
  };

  /**
   * A reply with text: one bubble per message (the brain's text before a tool call and after it
   * are two), all with the reply's key. Only the first has the "Update" tag; only the last the
   * error, and the "interrupted" / "not spoken" label (the others are `vk-more`).
   */
  const buildReply = (e: AssistantEntry, said: number): HTMLLIElement[] => {
    const cls = ["assistant"];
    if (e.interrupted) cls.push("interrupted");
    if (e.unspoken) cls.push("unspoken");
    if (e.unprompted) cls.push("unprompted");
    if (e.canned) cls.push("canned");
    const view = model.speechView(e.key);
    const karaoke = !!view && view.parts.length > 0;
    const messages = splitMessages(karaoke ? view.parts : [{ kind: "plain", text: e.text }]);
    if (!messages.length) messages.push([]); // only an error to show
    let toLight = said;
    return messages.map((parts, i) => {
      const li = document.createElement("li");
      li.className = (i < messages.length - 1 ? [...cls, "vk-more"] : cls).join(" ");
      li.dataset.key = e.key;
      li.dataset.reply = String(e.replyId);
      if (e.unprompted && i === 0) {
        const tag = document.createElement("span");
        tag.className = "tag";
        tag.textContent = "Update";
        li.append(tag);
      }
      if (!karaoke) {
        for (const p of parts) li.append(document.createTextNode(p.text));
      } else {
        const spans: HTMLElement[] = [];
        for (const p of parts) {
          const span = document.createElement("span");
          span.textContent = p.text;
          if (p.kind === "word") {
            span.className = spans.length < toLight ? "w said" : "w";
            span.dataset.at = String(p.at);
            spans.push(span);
          } else if (p.kind === "pending" || p.late) {
            // Late text of a silenced or unspoken reply looks like its unspoken rest.
            span.className = "pending";
          } else {
            span.className = "plain"; // display-only: shown as is, never lit
          }
          li.append(span);
        }
        // Words only ever get appended, so the ones lit before a rebuild stay lit (an interrupted
        // reply's position no longer advances, so they would not light up again).
        const next = Math.min(toLight, spans.length);
        toLight -= next;
        spoken.set(li, { spans, next });
      }
      if (e.error && i === messages.length - 1) {
        const err = document.createElement("span");
        err.className = "error";
        err.textContent = e.error;
        li.append(err);
      }
      return li;
    });
  };

  const render = () => {
    const keep = new Set(model.entries);
    for (const [e, r] of rendered) {
      if (!keep.has(e)) {
        for (const li of r.lis) li.remove();
        rendered.delete(e);
      }
    }
    let prev: Element | null = null;
    for (const e of model.entries) {
      const rev = model.revision(e);
      let r = rendered.get(e);
      let focus: HTMLElement | null = null;
      if (!r) {
        r = { lis: build(e), rev };
        rendered.set(e, r);
      } else if (r.rev !== rev) {
        const said = r.lis.reduce((n, li) => n + (spoken.get(li)?.next ?? 0), 0);
        const lis = build(e, said);
        for (const li of r.lis) focus ??= focusedIn(li);
        r.lis[0].replaceWith(lis[0]);
        for (const li of r.lis.slice(1)) li.remove();
        r.lis = lis;
        r.rev = rev;
      }
      for (const li of r.lis) {
        const expected: Element | null = prev ? prev.nextElementSibling : list.firstElementChild;
        if (expected !== li) {
          if (prev) prev.after(li);
          else list.prepend(li);
        }
        prev = li;
      }
      if (focus) refocus(focus, r.lis[0]);
    }
    // A new turn (the user starts or finishes speaking) brings the view back to the bottom.
    const last = model.entries.at(-1);
    if ((last?.role === "user" || last?.role === "ignored") && (model.entries.length !== lastCount || (last.role === "user" && last.partial))) follow.newTurn();
    lastCount = model.entries.length;
    if (follow.shouldAutoScroll()) toBottom();
    paint();
  };

  let scheduled = false;
  const offChange = model.onChange(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      render();
    });
  });

  const paint = () => {
    if (!list.isConnected || list.offsetParent === null) return; // hidden panel: nothing to do
    if (follow.shouldAutoScroll() && !atBottom(getScroller(), 2)) toBottom(); // opened or resized
    const live = progress();
    const liveKey = live ? model.keyOf(live.replyId) : null;
    // Light up words from where we left off; only replies that can still be playing (the last
    // three, each with all its messages' bubbles) are checked.
    const replies = list.querySelectorAll<HTMLElement>("li.assistant");
    const recent = new Set<string>();
    for (let i = replies.length - 1; i >= 0; i--) {
      const li = replies[i];
      const key = li.dataset.key!;
      if (!recent.has(key)) {
        if (recent.size === 3) break;
        recent.add(key);
      }
      const state = spoken.get(li);
      if (!state || state.next >= state.spans.length) continue;
      const ms = live && liveKey === key ? live.msPlayed : (model.speechView(key)?.playedMs ?? 0);
      while (state.next < state.spans.length && Number(state.spans[state.next].dataset.at) <= ms) {
        state.spans[state.next++].classList.add("said");
      }
    }
  };

  let raf = 0;
  const loop = () => {
    paint();
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  render();

  return {
    destroy() {
      cancelAnimationFrame(raf);
      offChange();
      ac.abort();
      list.replaceChildren();
    },
  };
}

/**
 * Kit 0.13: "Sent late" under a marked line sent after later turns (or long after it was said):
 * it stays where it was said, and the assistant's answer to it comes after the rest.
 */
export function sentLateLabel(): HTMLElement {
  const s = document.createElement("span");
  s.className = "vk-sent-late";
  s.textContent = "Sent late";
  s.title = "Sent to the assistant after what you said next; it was told this came first";
  return s;
}

/** Kit 0.12: "Send to assistant" on a marked line that was not sent on its own. */
export function sendButton(ignoredId: string, act: (id: string) => Promise<void>): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "vk-send-mine";
  b.dataset.ignored = ignoredId;
  b.textContent = "Send to assistant";
  b.title = "Yours now, but not sent: it wasn't the last thing you said. Send it anyway?";
  b.addEventListener("click", (ev) => {
    ev.stopPropagation();
    b.disabled = true;
    act(ignoredId).catch(() => (b.disabled = false));
  });
  return b;
}

const SVG = "http://www.w3.org/2000/svg";

let groupSeq = 0;

/** The button in `li` that has the keyboard focus, if any (to give it back once `li` is redrawn). */
function focusedIn(li: HTMLElement): HTMLElement | null {
  const a = document.activeElement;
  return a instanceof HTMLElement && a !== li && li.contains(a) ? a : null;
}

/**
 * Gives the focus to the redrawn `li`'s counterpart of `was` (the same kind of button for the
 * same speech), or else to its group toggle, so redrawing a line never drops a keyboard user.
 */
function refocus(was: HTMLElement, li: HTMLElement): void {
  const kind = was.classList[0];
  const same = [...li.querySelectorAll<HTMLElement>("button")].find(
    (b) => b.classList[0] === kind && b.dataset.clip === was.dataset.clip && b.dataset.ignored === was.dataset.ignored,
  );
  (same ?? li.querySelector<HTMLElement>(".vk-group-toggle"))?.focus({ preventScroll: true });
}

/** The disclosure chevron of a group marker (points down; turned up when open, by CSS). */
function chevron(): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("class", "vk-chevron");
  svg.setAttribute("viewBox", "0 0 12 12");
  svg.setAttribute("width", "10");
  svg.setAttribute("height", "10");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG, "path");
  path.setAttribute("d", "M2.5 4.5 6 8l3.5-3.5");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.8");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}

/** The files sent with a turn: a thumbnail for each image that has one, a chip for the rest. */
export function attachmentList(files: AttachmentInfo[]): HTMLElement {
  const box = document.createElement("span");
  box.className = "files";
  for (const f of files) {
    if (f.thumb?.startsWith("data:image/")) {
      const img = document.createElement("img");
      img.className = "thumb";
      img.src = f.thumb;
      img.alt = f.name;
      img.title = f.name;
      box.append(img);
      continue;
    }
    const chip = document.createElement("span");
    chip.className = "file";
    const kind = document.createElement("b");
    showKind(kind, f.mime, f.name);
    chip.append(kind, document.createTextNode(` ${f.name}`));
    chip.title = `${f.name} (${fileSize(f.size)})`;
    box.append(chip);
  }
  return box;
}

/**
 * A short label for a file's type: PDF, IMG, JSON, and for text its extension (MD, CSV, TXT).
 * Any other type (a program, an archive, a Word file, no type at all) is "FILE": it is drawn as
 * a generic file icon ({@link showKind}).
 */
export function fileKind(mime: string, name = ""): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return "IMG";
  if (mime === "application/json") return "JSON";
  if (mime.startsWith("text/")) return /\.([a-z0-9]{1,4})$/i.exec(name)?.[1]?.toUpperCase() ?? "TXT";
  return "FILE";
}

/**
 * Fills `el` with a file's kind: its label (PDF, IMG, MD, ...), or for any other type a generic
 * file icon (class `generic`, labelled "File").
 */
export function showKind(el: HTMLElement, mime: string, name = ""): void {
  const kind = fileKind(mime, name);
  if (kind !== "FILE") {
    el.textContent = kind;
    return;
  }
  el.classList.add("generic");
  el.setAttribute("role", "img");
  el.setAttribute("aria-label", "File");
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("aria-hidden", "true");
  for (const d of ["M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z", "M14 3v5h5"]) {
    const path = document.createElementNS(SVG, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "2");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
  }
  el.replaceChildren(svg);
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A turn split by voice: the user's words as they are, another voice's inline and set apart
 * (dimmed, labelled "Ignored: not your voice"), so the turn reads in the order it was said. With
 * guest markers (1.8), the other voice is only named ("David spoke"): their words are under Guests.
 */
function spanNodes(spans: SpeakerSpan[], model: TranscriptModel): Node[] {
  const out: Node[] = [];
  spans.forEach((s, i) => {
    if (i > 0) out.push(document.createTextNode(" "));
    if (s.mine) {
      out.push(document.createTextNode(s.text));
      return;
    }
    const other = document.createElement("span");
    other.className = "other-voice";
    const who = s.guest_id ? model.guestLabel(s.guest_id) : null;
    if (s.guest_id) other.dataset.guest = s.guest_id;
    if (model.guestMarkers) {
      other.classList.add("marker");
      other.textContent = `${who ?? "Someone"} spoke`;
      other.title = "Not your voice: not sent to the assistant. What they said is under Guests.";
      out.push(other);
      return;
    }
    other.title = `${who ? `${who}: ` : "Ignored: "}not your voice (voice match ${s.score.toFixed(2)})`;
    other.setAttribute("aria-label", `${who ? `${who}, ` : "Ignored, "}not your voice: ${s.text}`);
    other.textContent = s.text || "\u2026";
    out.push(other);
  });
  return out;
}
